
import { Edges, Image, Line, shaderMaterial, Text, useScroll, useTexture, useIntersect, MeshTransmissionMaterial } from "@react-three/drei"
import { extend, useFrame } from "@react-three/fiber"
import gsap from "gsap"
import { useEffect, useRef } from "react"
import textVertexShader from '../../shaders/text/vertex.glsl'
import textFragmentShader from '../../shaders/text/fragment.glsl'
import { useControls } from "leva"
import { DoubleSide } from "three"

import { mainStore } from "../../store"
import { __ } from "../../utils"
import Team from "../../components/team"


export default function HeadChapterOne() {

    // Texture loader
    const bgTexture = useTexture(import.meta.env.BASE_URL + 'static/text-texture.webp')

    // Utils
    const data = useScroll()

    const colors = mainStore.useStore(s => s.colors)
    const fonts = mainStore.useStore(s => s.fonts)
    const scrollMap = mainStore.useStore(s => s.scrollMap)

    let isBgReveal = false

    // Controls
    const { uTextFrequency, uTextElevation, uTextSpeed, uDisplaceX, uDisplaceY, uDisplaceSpeed, uDisplaceStep, uDisplaceClamp } = useControls('headText', {
        uDisplaceX: {
            value: 3.42,
            step: 0.001,
            min: 0,
            max: 20
        },
        uDisplaceY: {
            value: 3.58,
            step: 0.001,
            min: 0,
            max: 20
        },
        uDisplaceSpeed: {
            value: 0.1,
            step: 0.001,
            min: 0,
            max: 20
        },
        uDisplaceStep: {
            value: 0.7,
            step: 0.001,
            min: -5,
            max: 20
        },
        uDisplaceClamp: {
            value: -5,
            step: 0.001,
            min: -5,
            max: 20
        },
        uTextFrequency: {
            value: 0.59,
            step: 0.001,
            min: 0,
            max: 20
        },
        uTextElevation: {
            value: 0,
            step: 0.001,
            min: 0,
            max: 20
        },
        uTextSpeed: {
            value: 0.58,
            step: 0.001,
            min: 0,
            max: 20
        },
    })

    const config = useControls('Transmission Plane', {
        meshPhysicalMaterial: false,
        transmissionSampler: true,
        backside: true,
        samples: { value: 14, min: 1, max: 32, step: 1 },
        resolution: { value: 256, min: 256, max: 2048, step: 256 },
        transmission: { value: 1, min: 0, max: 1 },
        roughness: { value: 0.04, min: 0, max: 1, step: 0.01 },
        thickness: { value: 9.54, min: 0, max: 10, step: 0.01 },
        ior: { value: 1.93, min: 1, max: 5, step: 0.01 },
        chromaticAberration: { value: 0.06, min: 0, max: 1 },
        anisotropy: { value: 0.1, min: 0, max: 1, step: 0.01 },
        distortion: { value: 1, min: 0, max: 1, step: 0.01 },
        distortionScale: { value: 0.46, min: 0.01, max: 1, step: 0.01 },
        temporalDistortion: { value: 0, min: 0, max: 1, step: 0.01 },
        clearcoat: { value: 1, min: 0, max: 1 },
        attenuationDistance: { value: 1.79, min: 0, max: 10, step: 0.01 },
        attenuationColor: '#ffffff',
        color: '#ffffff',
        bg: '#839681'
    })

    // Refs
    const titleRef = useRef()
    const subTitleRef = useRef()
    const leftLineRef = useRef()
    const rightLineRef = useRef()
    const bgRef = useRef()
    const paperRef = useRef()
    const meshRef = useRef()

    // Intersect
    const chevronRef = useIntersect((visible) => visible ? mainStore.hide() : mainStore.reveal())

    // Shader
    const ImageShaderMaterial = shaderMaterial(
        { uTime: 0, uTextFrequency, uTextElevation, uTextSpeed, uDisplaceX, uDisplaceY, uDisplaceSpeed, uDisplaceStep, uDisplaceClamp },
        textVertexShader,
        textFragmentShader
    )

    useEffect(() => {

        gsap.fromTo(bgRef.current,
            {
                fillOpacity: 0
            },
            {
                fillOpacity: 0.15,
                duration: .5,
                delay: .5,
                onComplete: () => isBgReveal = true
            }
        )

        gsap.fromTo(bgRef.current.material,
            {
                uDisplaceStep: 1,

            },
            {
                uDisplaceStep: - 0.7,
                duration: 3,
            }
        )

        gsap.fromTo(bgRef.current.material,
            {
                uDisplaceClamp: -5,
                uDisplaceStep: - 0.7
            },
            {
                uDisplaceClamp: 0.7,
                uDisplaceStep: 0.1,
                duration: 3,
                delay: 3.5
            }
        )

        gsap.fromTo(titleRef.current,
            {
                fillOpacity: 0,
            },
            {
                fillOpacity: 1,
                duration: 6
            }
        )

        gsap.fromTo(titleRef.current.position,
            {
                y: -1,
                z: 5
            },
            {
                z: 0,
                y: 0,
                duration: 2,
                ease: 'inout'
            }
        )

        gsap.fromTo(subTitleRef.current,
            {
                fillOpacity: 0
            },
            {
                fillOpacity: 1,
                duration: 1.5,
                delay: 2
            }

        )

        gsap.fromTo(leftLineRef.current.position, {
            x: -1,
        },
            {
                x: 0,
                duration: .7,
                delay: 1,
                ease: 'out'
            })


        gsap.fromTo(rightLineRef.current.position,
            {
                x: 1,
            },
            {
                x: 0,
                duration: .7,
                delay: 1,
                ease: 'out'
            })

    }, [])

    useFrame((state, delta) => {

        // console.log(data.scroll)

        // title 
        titleRef.current.position.z = 0 + data.range(0, 0.1 * scrollMap.one / scrollMap.one) * 3
        subTitleRef.current.position.z = 0 + data.range(0, 0.1 * scrollMap.one / scrollMap.one) * 3

        // Square
        meshRef.current.position.z = -3 + data.range(0, 0.1 * scrollMap.one / scrollMap.one) * 5
        meshRef.current.rotation.z += delta * 0.2
        meshRef.current.rotation.y += delta * 0.2

        // Text background
        bgRef.current.material.uTime += delta
        paperRef.current.material.opacity = .45 - data.range(0, 0.1 * scrollMap.one / scrollMap.one) * 5
        if (isBgReveal) {
            bgRef.current.fillOpacity = 0.15 - data.range(0, 0.1 * scrollMap.one / scrollMap.one) * 0.7
        }

    })

    extend({ ImageShaderMaterial })

    return <>
        <group position={[0, 0, 1]}>
            <Text
                ref={titleRef}
                strokeWidth={0.001}
                fillOpacity={0}
                maxWidth={1}
                lineHeight={0.8}
                fontSize={0.29}
                position={[0, 0, 1]}
                font={fonts.cm.title}
                color={colors.title}
            >
                {__('GENÈSE', 'ORIGINS')}
            </Text>
            <Text
                ref={subTitleRef}
                maxWidth={1}
                lineHeight={0.8}
                fontSize={0.059}
                position={[0, -0.16, 1]}
                font={fonts.ms.text}
                color={colors.title}
            >
                1984 - 1990
            </Text>
            <group position={[0, 0, 3]}>
                <Line
                    ref={leftLineRef}
                    points={[[-1, 0, 0], [-0.17, 0, 0]]}
                    color={colors.title}
                    lineWidth={1}
                    position={[-1, 0, 0]}
                />
                <Line
                    ref={rightLineRef}
                    points={[[0.17, 0, 0], [1, 0, 0]]}
                    color={colors.title}
                    lineWidth={1}
                />
            </group>
            <mesh ref={meshRef} scale={1.5} position-z={-3}>
                <planeGeometry />
                <Edges
                    linewidth={0.7}
                    scale={1.1}
                    threshold={15} // Display edges only when the angle between two faces exceeds this value (default=15 degrees)
                    color="black"
                    opacity={.15}
                />
                <meshBasicMaterial side={DoubleSide} visible={true} color={colors.clear} />
                {/* <MeshTransmissionMaterial  {...config} /> */}

            </mesh>

            <Image
                ref={paperRef}
                url={import.meta.env.BASE_URL + "static/paper-texture.webp"}
                transparent
                scale={20}
                opacity={0.5}
                zoom={0.6}
                segments={4}
                position={[0, 0, -8]}

                toneMapped={false}
                color={colors.clear}
            >
                <planeGeometry />
            </Image>

            {
                __(
                    <Text
                        ref={bgRef}
                        position={[0, -0.5, -5]}
                        maxWidth={8}
                        color={'black'}
                        scale={1}
                        font={fonts.cd.title}
                        fontSize={0.23}
                        textAlign="center"
                        fillOpacity={0.15}
                        rotation={[- Math.PI * 0.0, 0, Math.PI * 0.00]}
                        lineHeight={3.6}
                    >

                        Création de l’entreprise Silab
                        L’Histoire de silab commence dans un atelier rue Charles Brun, à Brive-la-Gaillarde (France).
                        L’entreprise commercialise dans un premier temps un médicament vétérinaire basé sur la valorisation du sang animal.
                        Commercialisation du 1er actif cosmétique
                        silab propose de la SAB (Sérum albumine bovine), une protéine reconnue pour ses effets liftant et tenseur.



                        <imageShaderMaterial attach="material" uDisplaceClamp={uDisplaceClamp} uDisplaceStep={uDisplaceStep} uDisplaceSpeed={uDisplaceSpeed} uDisplaceX={uDisplaceX} uDisplaceY={uDisplaceY} uTextElevation={uTextElevation} uTextFrequency={uTextFrequency} uTextSpeed={uTextSpeed} />
                    </Text>,
                    <Text
                        ref={bgRef}
                        position={[0, -0.5, -5]}
                        maxWidth={8}
                        color={'black'}
                        scale={1}
                        font={fonts.cd.title}
                        fontSize={0.23}
                        textAlign="center"
                        fillOpacity={0.15}
                        rotation={[- Math.PI * 0.0, 0, Math.PI * 0.00]}
                        lineHeight={3.6}
                    >

                        Founding of Silab
                        The story of silab begins in a workshop, in Brive-la-Gaillarde (France).
                        At first, the company fmarkets a veterinary medicinal product based on animal blood.
                        Launch of the 1st cosmetic active ingredient
                        silab offers a Serum bovine albumin, a protein renowned for its lifting and tensors effects.


                        <imageShaderMaterial attach="material" uDisplaceClamp={uDisplaceClamp} uDisplaceStep={uDisplaceStep} uDisplaceSpeed={uDisplaceSpeed} uDisplaceX={uDisplaceX} uDisplaceY={uDisplaceY} uTextElevation={uTextElevation} uTextFrequency={uTextFrequency} uTextSpeed={uTextSpeed} />
                    </Text>
                )
            }



            <Image
                ref={chevronRef}
                url={import.meta.env.BASE_URL + "static/icons/down.svg"}
                scale={0.08}
                opacity={.5}
                position={[0, -.87, 2]}
                transparent
            />

        </group>
    </>

}